import React, { Fragment, useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  HStack,
  Checkbox,
  TableContainer,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  SortingState,
  getSortedRowModel,
  getExpandedRowModel,
  ColumnDef,
  Row,
} from "@tanstack/react-table";
import isEmpty from "lodash.isempty";
import { SortUpIcon, SortDownIcon } from "@root/assets/icons";
import { testId as testIdFn } from "src/utils/e2e";
import { Paginate } from "../pagination";
import NoRecordsText from "../common/text/noRecordsText";

type DataTableProps<Data extends object> = {
  data: TObject[];
  columns: ColumnDef<Data, TObject>[];
  renderSubComponent?: (props: { row: Row<Data> }) => React.ReactElement;
  getRowCanExpand?: (row: Row<Data>) => boolean;
  getRowSelected?: (row: Row<Data>) => boolean;
  onRowSelectionChange?: (row: Row<Data>) => boolean;
  onRowSelectionChangeHandler?: (row: TObject) => void;
  pageCount?: TObject;
  page?: TObject;
  pageSize?: TObject;
  handlePageChange?: TObject;
  testId?: string;
  getRowId?: TObject;
  centeredCols?: Array<string>;
  canEditRow?: boolean;
  showPagination?: boolean;
  headerColors?: TObject;
  isRtl?: boolean;
  showFooter?: boolean;
  includeCheckedBoxColumn?: boolean;
  resetCheckBoxes?: boolean;
  onSelectRowThroughCheckbox?: any;
  getAllCheckedData?: boolean;
  selectedData?: TArrayOfObjects;
  isDisabledCheckBox?: boolean;
  defaultSortState?: TObject[];
  theadBg?: any;
};

export function DataTable<Data extends object>({
  data,
  columns,
  getRowCanExpand,
  renderSubComponent,
  page,
  pageSize,
  pageCount,
  handlePageChange,
  testId,
  getRowId,
  showPagination = true,
  onRowSelectionChangeHandler,
  centeredCols = [],
  includeCheckedBoxColumn = false,
  canEditRow = true,
  headerColors = {},
  isRtl = false,
  showFooter = false,
  resetCheckBoxes = false,
  getAllCheckedData = false,
  onSelectRowThroughCheckbox,
  selectedData,
  theadBg,
  isDisabledCheckBox = false,
  defaultSortState = [],
}: DataTableProps<Data>) {
  const [t] = useTranslation("common");
  const [sorting, setSorting] = React.useState<SortingState>(defaultSortState);

  const [expanded, setExpanded] = useState({});
  const [allChecked, setAllChecked] = useState(false);

  const [rowSelection, setRowSelection] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [hoveredRowId, setHoveredRowId] = useState<string | number | null>(
    null
  );

  // Clear hoveredRowId when data changes to prevent theme access issues during re-renders
  useEffect(() => {
    setHoveredRowId(null);
  }, [data]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    onRowSelectionChange: canEditRow ? setRowSelection : undefined,
    getRowCanExpand,
    state: {
      sorting,
      expanded,
      rowSelection,
    },
    meta: {
      hoveredRowId,
    },
    getSubRows: () => undefined,
    getRowId,
  });

  useEffect(() => {
    if (!isEmpty(rowSelection) && onRowSelectionChangeHandler) {
      onRowSelectionChangeHandler(
        table.getSelectedRowModel().flatRows[0]?.original
      );
      !includeCheckedBoxColumn && setRowSelection({});
    }
  }, [
    onRowSelectionChangeHandler,
    rowSelection,
    table,
    includeCheckedBoxColumn,
  ]);

  useEffect(() => {
    if (includeCheckedBoxColumn && !getAllCheckedData) {
      const updatedRowSelection: { [key: string]: boolean } = {};
      table.getRowModel().rows.map((row: any) => {
        updatedRowSelection[row.original.id] = false;

        if (onSelectRowThroughCheckbox && row?.original) {
          onSelectRowThroughCheckbox(row?.original, false);
        }
      });

      setRowSelection(updatedRowSelection);

      setAllChecked(false);
    }
  }, [includeCheckedBoxColumn, page, table.getRowModel().rows]);

  useEffect(() => {
    if (resetCheckBoxes && includeCheckedBoxColumn) {
      setRowSelection((prevRowSelection) => {
        const updatedRowSelection: { [key: string]: boolean } = {};

        Object.keys(prevRowSelection).forEach((rowId) => {
          updatedRowSelection[rowId] = false;
        });

        return updatedRowSelection;
      });
    }
  }, [resetCheckBoxes]);

  const sendOriginalRow = (rowId: any, check: boolean) => {
    const originalRow = table
      .getRowModel()
      .rows.find((row: TObject) => row.original?.id === rowId);

    if (onSelectRowThroughCheckbox && originalRow) {
      onSelectRowThroughCheckbox(originalRow?.original, check);
    }
  };

  const sendAllDataOnSelectAll = (select: boolean) => {
    const originalRow: any = table
      .getRowModel()
      .rows.map((item: TObject) => ({ ...item.original, select }));

    if (onSelectRowThroughCheckbox && originalRow) {
      onSelectRowThroughCheckbox(originalRow, select);
    }
  };

  const setSelectionData = (sData: TArrayOfObjects) => {
    const finalSelected: TObject = {};
    const original = table.getRowModel().rows;

    original.forEach((row: TObject) => {
      const { id } = row.original;
      const ifExist = sData?.find(
        (info: any) => info.id === id && info?.select
      );

      finalSelected[id] = ifExist?.select || false;
    });

    const allSelected = Object.values(finalSelected)?.every(
      (item: any) => item
    );

    if (allSelected) setAllChecked(true);
    else setAllChecked(false);

    setRowSelection((prev: any) => ({ ...prev, ...finalSelected }));
  };

  const handleSelectAll = (event: TObject) => {
    const { checked } = event.target;

    setAllChecked(checked);

    setRowSelection((prevRowSelection) => {
      const updatedRowSelection: { [key: string]: boolean } = {};

      Object.keys(prevRowSelection).forEach((rowId) => {
        updatedRowSelection[rowId] = checked;

        if (prevRowSelection[rowId] !== updatedRowSelection[rowId]) {
          sendOriginalRow(rowId, checked);
        }
      });

      return updatedRowSelection;
    });

    sendAllDataOnSelectAll(checked);
  };

  // For Search and Select
  useEffect(() => {
    data?.length && selectedData?.length && setSelectionData(selectedData);
    data?.length &&
      !selectedData?.length &&
      Object.keys(rowSelection).length !== 0 &&
      setRowSelection({});
  }, [
    includeCheckedBoxColumn,
    selectedData?.length,
    data?.length,
    selectedData,
  ]);

  const sendupdatedDataOnSelectOne = (selectedItems: TObject) => {
    const originalRow: any = table.getRowModel().rows.map((rData: any) => ({
      ...rData?.original,
      select: selectedItems[rData?.original?.id],
    }));

    const isAllSelected = originalRow?.every((item: any) => item.select);

    if (onSelectRowThroughCheckbox && originalRow) {
      onSelectRowThroughCheckbox(originalRow, isAllSelected);
    }
  };

  useEffect(() => {
    if (includeCheckedBoxColumn && !getAllCheckedData) {
      setRowSelection((prevRowSelection) => {
        const updatedRowSelection: { [key: string]: boolean } = {};

        Object.keys(prevRowSelection).forEach((rowId) => {
          updatedRowSelection[rowId] = allChecked;

          if (prevRowSelection[rowId] !== updatedRowSelection[rowId]) {
            sendOriginalRow(rowId, allChecked);
          }
        });

        return updatedRowSelection;
      });
    }
  }, [includeCheckedBoxColumn, allChecked, getAllCheckedData]);

  return (
    <Box>
      <TableContainer
        border="1px solid"
        borderColor="gray.200"
        borderRadius="12px"
        overflow="hidden"
        bg="white"
      >
        <Table size="lg" bg="white" {...(testId ? testIdFn(testId) : {})}>
          <Thead
            bg={theadBg || "#CDD6DC50"}
            borderBottom="1px solid"
            borderColor="gray.200"
            zIndex={5}
          >
            {table?.getHeaderGroups()?.map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {includeCheckedBoxColumn && (
                  <Th
                    borderTopStartRadius="12px"
                    py="16px"
                    px="24px"
                    color="gray.700"
                    fontWeight={500}
                    fontSize="14px"
                  >
                    <Checkbox
                      isDisabled={isDisabledCheckBox}
                      background={allChecked ? "blue" : "white"}
                      borderColor={allChecked ? "blue" : "white"}
                      colorScheme="white"
                      isChecked={allChecked}
                      onChange={(event) => handleSelectAll(event)}
                      {...testIdFn(`data-table-header-checkbox-${data[0]?.id}`)}
                    />
                  </Th>
                )}
                {headerGroup.headers.map((header, i) => {
                  const { meta }: TObject = header.column.columnDef;
                  const isActionsColumn = header.column.id === "actions";

                  const headerTextAlignment = () => {
                    if (centeredCols.includes(header.column.id))
                      return "center";
                    if (meta?.alignRight) return "right";
                  };

                  return (
                    <Th
                      px={isActionsColumn ? "16px" : "24px"}
                      py="16px"
                      borderTopStartRadius={
                        i === 0 && !includeCheckedBoxColumn ? "12px" : "0"
                      }
                      borderTopEndRadius={
                        i === headerGroup.headers.length - 1 ? "12px" : "0"
                      }
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      isNumeric={meta?.isNumeric}
                      color={
                        headerColors?.headerText
                          ? headerColors.headerText
                          : "#231F20"
                      }
                      fontWeight={500}
                      fontSize="14px"
                      textAlign={headerTextAlignment()}
                      borderColor="gray.200"
                      textTransform={"capitalize"}
                      display={isActionsColumn ? "flex" : "table-cell"}
                      width={isActionsColumn ? "1%" : undefined}
                      minWidth={isActionsColumn ? "auto" : undefined}
                      whiteSpace={isActionsColumn ? "nowrap" : undefined}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        width={isActionsColumn ? "fit-content" : "100%"}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-start"
                          width={isActionsColumn ? "fit-content" : "100%"}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <span>
                            {header.column.getIsSorted() ||
                            header.column.columnDef.enableSorting ? (
                              header.column.getIsSorted() === "desc" ? (
                                <SortDownIcon
                                  ml="3"
                                  aria-label="sorted descending"
                                />
                              ) : (
                                <SortUpIcon
                                  ml="3"
                                  aria-label="sorted ascending"
                                />
                              )
                            ) : null}
                          </span>
                        </Box>
                      </Box>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.length &&
            table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row: any) => (
                <Fragment key={row.id}>
                  <Tr
                    {...testIdFn(`data-table-row-${row?.original?.id}`)}
                    key={row.id}
                    _hover={{
                      backgroundColor: "gray.100",
                    }}
                    bg={"white"}
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    onMouseEnter={() => {
                      const rowId = row.original?.id ?? row.id;
                      if (hoveredRowId !== rowId) {
                        setHoveredRowId(rowId);
                      }
                    }}
                    onMouseLeave={() => {
                      if (hoveredRowId !== null) {
                        setHoveredRowId(null);
                      }
                    }}
                  >
                    {includeCheckedBoxColumn && (
                      <Td p={0}>
                        <Checkbox
                          isDisabled={isDisabledCheckBox}
                          colorScheme="brand"
                          bg="white"
                          {...testIdFn(
                            `data-table-row-checkbox-${row?.original?.id}`
                          )}
                          isChecked={rowSelection[row.original.id]}
                          onChange={(event) => {
                            setRowSelection((prevRowSelection) => {
                              const res = {
                                ...prevRowSelection,
                                ...(row.original.id
                                  ? { [row.original.id]: event.target.checked }
                                  : { [row.id]: event.target.checked }),
                              };

                              getAllCheckedData &&
                                sendupdatedDataOnSelectOne(res);
                              return res;
                            });
                            !getAllCheckedData &&
                              sendOriginalRow(
                                row.original.id,
                                event.target.checked
                              );
                          }}
                        />
                      </Td>
                    )}
                    {row.getVisibleCells().map((cell: any) => {
                      const { meta }: TObject = cell.column.columnDef;
                      const isActionsCell = cell.column.id === "actions";

                      if (row.depth === 0) {
                        const getCellAlignment = () => {
                          if (centeredCols.includes(cell.column.id))
                            return "center";
                          if (meta?.alignRight) return "right";
                          return "inherit";
                        };

                        return (
                          <Td
                            minHeight="52px"
                            key={cell.id}
                            isNumeric={meta?.isNumeric}
                            px={isActionsCell ? "16px" : "24px"}
                            fontSize="16px"
                            fontWeight={400}
                            color="gray.500"
                            textAlign={
                              isActionsCell ? "left" : getCellAlignment()
                            }
                            maxW={
                              isActionsCell
                                ? "none"
                                : meta?.alignRight
                                ? "none"
                                : "200px"
                            }
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            wordBreak="break-word"
                            borderColor="gray.200"
                            width={isActionsCell ? "1%" : undefined}
                            minWidth={isActionsCell ? "auto" : undefined}
                          >
                            {meta?.alignRight ? (
                              flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )
                            ) : (
                              <Text
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                                title={
                                  typeof flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  ) === "string"
                                    ? String(
                                        flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext()
                                        )
                                      )
                                    : undefined
                                }
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </Text>
                            )}
                          </Td>
                        );
                      }
                    })}
                  </Tr>
                  {row.getIsExpanded() && renderSubComponent && (
                    <Tr key={row.id}>
                      <Td colSpan={row.getVisibleCells().length}>
                        {renderSubComponent({ row })}
                      </Td>
                    </Tr>
                  )}
                </Fragment>
              ))
            ) : (
              <Tr>
                <Td colSpan={columns.length} textAlign="center" p="10">
                  <NoRecordsText mt={"0px"} />
                </Td>
              </Tr>
            )}

            {showFooter
              ? table.getFooterGroups().map((footerGroup) => (
                  <Tr key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <Td
                        minHeight="52px"
                        key={header.id}
                        p="3"
                        fontSize="12px"
                        fontWeight={700}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.footer,
                              header.getContext()
                            )}
                      </Td>
                    ))}
                  </Tr>
                ))
              : null}
          </Tbody>
        </Table>
      </TableContainer>
      {showPagination ? (
        <HStack justifyContent="center" alignItems="center">
          <Paginate
            page={page}
            count={pageCount}
            pageSize={pageSize}
            onPageChange={(page: number) => {
              handlePageChange(page);
            }}
            margin={1}
            variant="ghost"
            isRtl={isRtl}
          />
        </HStack>
      ) : null}
    </Box>
  );
}
